/** @flow */
import React from "react";
import { localize } from "redux-i18n";

import { addDays } from "date-fns";
import uiSchema from "./ui-schema";
import { Button, Text, View, TooltipDropdown } from "@components";
import { SchemaForm } from "@components";
import { FormHeader } from "@modules/client/components";
import type {
  ClientServiceAbsenceManagement,
  FormResponse,
} from "@modules/client/reducer/service/types";
import { cloneObject } from "@utils/object";
import { emailPattern } from "@utils/schema";
import config from "@config";
type State = $Exact<{
  isEnabled: boolean | null,
  formData: Object,
  edit: boolean,
}>;
type Props = $Exact<{
  ...PropsFromLocalize,
  service: ClientServiceAbsenceManagement,
  schema: Object,
  disableEdit?: boolean,
  submitForm: ClientServiceAbsenceManagement => void,
}>;

export class ServiceAbsenceMainOptions extends React.Component<Props, State> {
  state = {
    isEnabled: null,
    formData: {},
    edit: false,
  };
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  submitForm = () => {
    this.formRef.current.submit();
  };
  componentDidMount() {
    if (this.props.service) {
      this.setState({
        formData: this.transformData(this.props.service),
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.service &&
      JSON.stringify(prevProps.service) !== JSON.stringify(this.props.service)
    ) {
      this.setState({
        formData: this.transformData(this.props.service),
      });
    }
  }

  toggleEdit = () => {
    this.setState({
      edit: !this.state.edit,
    });
  };

  cancelEdit = () => {
    this.setState({
      ...this.state,
      edit: false,
      formData: this.transformData(this.props.service),
    });
  };

  transformData = (data: Object) => {
    const clonedData = cloneObject(data);
    const settings = clonedData.settings;

    return {
      ...clonedData,
      serviceType: settings.serviceType,
      absenceLineNumber: settings.absenceLineNumber,
      blockConsent: settings.blockConsent,
      overflowSupport: settings.overflowSupport,
      emailAddress: settings.emailAddress,
      emailAppearAs: settings.emailAppearAs,
    };
  };

  transformSubmittedData = (formData: Object) => {
    let data = { ...formData, ...this.state.formData };
    data.settings.serviceType = data.serviceType;
    data.settings.absenceLineNumber = data.absenceLineNumber;
    data.settings.blockConsent = data.blockConsent;
    data.settings.overflowSupport = data.overflowSupport;
    data.settings.emailAddress = config.defaultNotificationAddress;
    data.settings.emailAppearAs = config.defaultEmailAppearAs;

    delete data.serviceType;
    delete data.absenceLineNumber;
    delete data.blockConsent;
    delete data.overflowSupport;
    delete data.emailAddress;
    delete data.emailAppearAs;
    if (data.isEnabled === true) data.activeTo = null;

    return data;
  };

  submitHandler = (formData: Object) => {
    const data = this.transformSubmittedData(formData);

    this.props.submitForm(data);
    this.toggleEdit();
  };

  handleChange = (formData: ClientServiceAbsenceManagement) => {
    this.setState({
      isEnabled: formData.isEnabled,
      formData,
    });
  };

  validateSchema = (formData: Object, errors: Object) => {
    if (formData.emailAddress && !emailPattern.test(formData.emailAddress)) {
      errors.emailAddress.addError("Invalid email format");
    }

    return errors;
  };

  get formData() {
    return this.state.formData;
  }

  get uiSchema() {
    const schema = uiSchema(this.props.t, this.formData.isEnabled, config);
    if (this.formData.isEnabled === true) {
      schema.activeTo.minDate = null;
      schema.activeTo["ui:widget"] = "hidden";
    } else {
      schema.activeTo.minDate = addDays(new Date(this.formData.activeFrom), 1);
      schema.activeTo["ui:widget"] = "date-picker";
    }

    return schema;
  }

  render() {
    return (
      <View gap={3}>
        <View direction="row" justifyContent="space-between" alignItems="center">
          <Text type="h5">{this.props.t("SERVICE_DETAILS")}</Text>
          {!this.props.disableEdit && (
            <View direction="row" justifyContent="space-between" gap="4">
              {!this.state.edit ? (
                <Button materialIcon="edit" onClick={this.toggleEdit}>
                  {this.props.t("EDIT")}
                </Button>
              ) : (
                <View direction="row" gap="4">
                  <Button color="secondary" onClick={this.cancelEdit}>
                    {this.props.t("CANCEL")}
                  </Button>
                  <Button
                    materialIcon="replay_circle_filled"
                    className="ml-2"
                    onClick={this.submitForm}
                  >
                    {this.props.t("SUBMIT")}
                  </Button>
                </View>
              )}
            </View>
          )}
        </View>
        {this.props.schema && (
          <SchemaForm
            externalSubmit
            onChange={this.handleChange}
            schema={this.props.schema}
            formData={this.formData}
            uiSchema={this.uiSchema}
            ref={this.formRef}
            onSubmit={this.submitHandler}
            readonly={!this.state.edit}
            validate={this.validateSchema}
            boxGrid
          />
        )}
      </View>
    );
  }
}

export default localize()(ServiceAbsenceMainOptions);
